<template>
  <div :class="wrapperClassName" v-if="visibleIfResult" id="wdw-library-form-control-slider">
    <template v-if="!hidden">
      <label :class="labelClassName" :for="idInput" v-dompurify-html="labelText" :aria-label="label"></label>

      <span
        class="help-trigger"
        :title="domainStore.translate('general.form.show-help-tooltip')"
        @click="toggleHelpText"
        v-if="help"
      >
        <font-awesome-icon icon="question-circle" size="lg" />
      </span>
      <Message
        v-show="shouldShowHelp"
        closable
        severity="info"
        role="alert"
        class="mb-2 form-info-message"
        @close="toggleHelpText"
      >
        <span v-dompurify-html="help"></span>
      </Message>
    </template>

    <input
      :id="idInput"
      :data-wdw-code="code"
      ref="input"
      type="number"
      :data-vv-as="labelText"
      :name="name"
      :required="required"
      :placeholder="placeholder"
      :class="inputClassName"
      v-model="value"
      tabindex="0"
      :aria-invalid="hasError || hasServerError"
      :aria-describedby="idErrorText"
      :hidden="hidden"
      :readonly="readonly"
      :min="min"
      :max="max"
    />
    <input
      :id="idInput + '-range'"
      type="range"
      :name="name + '-range'"
      :class="rangeClassName"
      v-model="value"
      tabindex="-1"
      :hidden="hidden"
      :disabled="readonly"
      :min="min"
      :max="max"
    />
    <template v-if="!hidden">
      <span :id="idErrorText" v-show="hasError" class="text-danger form-error-message" aria-atomic="true">
        {{ error }}
      </span>
      <span :id="idErrorText" v-show="hasServerError" class="text-danger form-error-message" aria-atomic="true">
        {{ serverError }}
      </span>
    </template>
  </div>
</template>

<script>
import { computed, watch, toRefs } from 'vue';
import { useField } from 'vee-validate';
import useInputValidate from '@/composables/general/useInputValidate';
import useToggler from '@/composables/general/useToggler';
import Message from 'primevue/message';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { domainStore } from '@/store/domain-store';
library.add(faQuestionCircle);

export default {
  name: 'form-control-input',

  props: {
    name: {
      required: true,
      type: String,
    },
    code: {
      required: false,
      type: String,
      default: '',
    },
    label: {
      required: false,
      type: String,
      default: '',
    },
    placeholder: {
      required: false,
      type: String,
      default: '',
    },
    defaultvalue: {
      required: false,
      type: Number,
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: '',
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    error: {
      required: false,
      type: String,
      default: '',
    },
    serverError: {
      required: false,
      type: String,
      default: '',
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    shouldcache: {
      required: false,
      type: Boolean,
      default: false,
    },
    hidden: {
      required: false,
      type: Boolean,
      default: false,
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-form-attr',
    },
    visibleIfResult: {
      required: false,
      type: Boolean,
      default: true,
    },
    readonly: {
      required: false,
      type: Boolean,
    },
    min: {
      required: false,
      type: Number,
    },
    max: {
      required: false,
      type: Number,
    },
  },

  components: { Message },
  emits: ['validate'],

  setup(props, context) {
    const {
      name,
      code,
      label,
      min,
      max,
      required,
      defaultvalue,
      error,
      serverError,
      additionalLabelClasses,
      shouldcache,
      readonly,
      prefixId,
    } = toRefs(props);

    const { isValidEmail, isValidURL } = useInputValidate();
    const { toggleValue: shouldShowHelp, toggle: toggleHelpText } = useToggler();

    //+---------------------+
    //|    Other functions  |
    //+---------------------+
    function validate(value) {
      // vee-validate's composition API
      const finalvalue = value ? value : '';
      const returnValue = {
        value: finalvalue,
        valueHuman: finalvalue,
        isLoggedIn: true,
        name: name.value,
        code: code.value,
      };
      if (required.value) {
        let validated = true;
        if (min.value && min.value > finalvalue) {
          validated = false;
        }
        if (max.value && max.value < finalvalue) {
          validated = false;
        }
        if (isNaN(finalvalue)) validated = false;

        returnValue.validated = validated;
      }

      if (shouldcache.value) {
        localStorage.setItem(name.value, returnValue.value);
      }

      // Emit event
      context.emit('validate', returnValue);
      return returnValue.validated ? true : error.value;
    }
    const { errorMessage, value } = useField(name, validate, {
      initialValue: defaultvalue.value,
    });

    watch(defaultvalue, (nV, oV) => {
      value.value = nV;
    });

    //+---------------------+
    //|    computed()       |
    //+---------------------+
    const hasError = computed(() => {
      return errorMessage.value != undefined;
    });
    const hasServerError = computed(() => {
      return serverError.value != undefined && serverError.value !== '';
    });
    const wrapperClassName = computed(() => {
      return {
        'form-group': true,
        'has-error': hasError.value || hasServerError.value,
      };
    });
    const labelClassName = computed(() => {
      return additionalLabelClasses.value + ' ' + (hasError.value || hasServerError.value ? 'text-danger' : '');
    });
    const inputClassName = computed(() => {
      return {
        'form-control': true,
        'is-invalid': hasError.value || hasServerError.value,
      };
    });
    const rangeClassName = computed(() => {
      return {
        'form-control': true,
        'is-invalid': hasError.value || hasServerError.value,
        'form-range': true,
        disabled: readonly.value,
      };
    });
    const labelText = computed(() => {
      return label.value + (required.value ? '*' : '');
    });
    const idInput = computed(() => {
      return prefixId.value + '-text-' + name.value;
    });
    const idErrorText = computed(() => {
      return prefixId.value + '-text-' + name.value + '-error';
    });

    //+---------------------+
    //|  Lifecycle Hooks    |
    //+---------------------+

    //+---------------------+
    //|    return data      |
    //+---------------------+
    return {
      domainStore,
      // Computed
      hasError,
      hasServerError,
      wrapperClassName,
      labelClassName,
      inputClassName,
      idInput,
      idErrorText,
      rangeClassName,
      labelText,
      // Methods
      toggleHelpText,
      // Variables
      shouldShowHelp,
      errorMessage,
      value,
    };
  },
};
</script>

<style lang="scss" scoped>
label {
  display: inline;
  font-weight: bold;
  max-width: 100%;
}
input {
  margin-top: 5px;
}
.form-group {
  margin-bottom: 15px;
}
.help-trigger {
  cursor: pointer;
  margin-left: 4px;
}
</style>
